import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ProductConfiguration } from '@/models/product';
import {
    CACHE_KEY_BULK_UPDATES,
    useGetProductConfigurationsQuery,
    usePostBulkUpdatesMutation,
} from '@/api/v2/productConfigurations';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNotification } from '@starjack/features/notifications';
import {
    BulkUpdateActionPayload,
    BulkUpdateType,
    ConfigurationCollection,
    ConfigurationState,
    initialConfigurationState,
    RemoveConditionConfiguration,
    SourceConfiguration,
} from '@/models/bulkUpdate';
import { useConfigurationPanelContext } from '@/pageContents/productConfiguration/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { ConfigurationsPanel } from '@/pageContents/productConfiguration/ProductConfigurationBulkUpdate/ConfigurationsPanel';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';

const plainIntegrationObject = () => ({
    barcode: false,
    internalProduction: false,
    keycard: false,
    pickup: false,
});

export const initialBookingOptions = {
    integrationConfiguration: plainIntegrationObject(),
    mobileConfiguration: plainIntegrationObject(),
    portalConfiguration: plainIntegrationObject(),
    b2bConfiguration: plainIntegrationObject(),
};

type ConfigurationsPanelContainerProps = {
    selectedConfigurations: ProductConfiguration[];
};

export const ConfigurationsPanelContainer: FunctionComponent<
    ConfigurationsPanelContainerProps
> = ({ selectedConfigurations }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: productConfigurations } = useGetProductConfigurationsQuery({
        liftOperatorId,
    });

    // prefetch for components inside panel
    useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorId,
    });

    const { addNotification } = useNotification();
    const [postBulkUpdate, { isLoading }] = usePostBulkUpdatesMutation({
        fixedCacheKey: CACHE_KEY_BULK_UPDATES,
    });
    const [configurationState, setConfigurationState] =
        useState<ConfigurationState>(initialConfigurationState);
    const [bookingConfiguration, setBookingConfiguration] =
        useState<ConfigurationCollection>(null);
    const [
        dynamicPricingSourceConfiguration,
        setDynamicPricingSourceConfiguration,
    ] = useState<SourceConfiguration>(null);
    const [removeConditions, setRemoveConditions] =
        useState<RemoveConditionConfiguration>(null);
    const [isActive, setIsActive] = useState<boolean>(null);
    const { setIsActive: setPanelActive } = useConfigurationPanelContext();

    const onPanelChange = (action: BulkUpdateActionPayload) => {
        switch (action.type) {
            case BulkUpdateType.ACTIVE_STATUS:
                setIsActive(action.isActive);
                break;
            case BulkUpdateType.SET_DYNAMIC_PRICING:
                setDynamicPricingSourceConfiguration(
                    action.sourceConfiguration
                );
                break;
            case BulkUpdateType.SET_REMOVE_CONDITION:
                setRemoveConditions(action.payload);
                break;
            case BulkUpdateType.SET_BOOKING_OPTIONS:
                setBookingConfiguration(action.configurations);
                break;
        }
    };

    const onSave = () => {
        postBulkUpdate({
            productIds: selectedConfigurations.map((sC) => sC.productId),
            liftOperatorId: liftOperatorId,
            enabled: isActive,
            sourceProduct: dynamicPricingSourceConfiguration?.productId
                ? dynamicPricingSourceConfiguration
                : null,
            removePriceConditions:
                removeConditions?.removePriceConditions || false,
            removeContingentConditions:
                removeConditions?.removeContingentConditions || false,
            ...(bookingConfiguration ? bookingConfiguration : {}),
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Konfigurationen erfolgreich aktualisiert',
                });
                setConfigurationState(initialConfigurationState);
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Aktualisieren. Versuchen Sie es später noch einmal',
                });
            });
    };

    useEffect(() => {
        setPanelActive(!!selectedConfigurations?.length);
    }, [selectedConfigurations]);

    const configurationsWithConditions = useMemo(
        () =>
            productConfigurations?.filter(
                (pC) =>
                    pC.contingentConditions.length || pC.priceConditions.length
            ),
        [productConfigurations]
    );

    return (
        <ConfigurationsPanel
            isActive={isActive}
            removeConditions={removeConditions}
            sourceConfiguration={dynamicPricingSourceConfiguration}
            numOfConfigurations={selectedConfigurations.length}
            configurationsWithConditions={configurationsWithConditions}
            configurationCollection={bookingConfiguration}
            onConfigurationsChange={onPanelChange}
            configurationState={configurationState}
            onConfigurationStateChange={(nextState) => {
                setConfigurationState(nextState);
            }}
            onSave={onSave}
            isLoading={isLoading}
        />
    );
};
